import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  ReactiveFormsModule,
} from '@angular/forms';
import { Observable, debounceTime, distinctUntilChanged, forkJoin } from 'rxjs';

import { toErrorMessage } from '../../shared/helpers/http-error';
import {
  byBusinessThenAlpha,
  isBusinessRole,
  stripRolePrefix,
} from '../../shared/helpers/role.helpers';
import { rsqlTextSearch } from '../../shared/helpers/rsql';
import {
  fullName as userFullName,
  initials as userInitials,
} from '../../shared/helpers/user.helpers';
import { RolesService } from '../../shared/services/roles.service';
import { UserRolesService } from '../../shared/services/user-roles.service';
import { UsersService } from '../../shared/services/users.service';
import { RoleDTO } from '../../shared/types/role.types';
import { User } from '../../shared/types/user.types';

const USER_SEARCH_FIELDS = [
  'firstName',
  'lastName',
  'username',
  'email',
  'phone',
] as const;

@Component({
  selector: 'app-role-assignments',
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './role-assignments.component.html',
})
export class RoleAssignmentsComponent implements OnInit {
  private readonly usersService = inject(UsersService);
  private readonly rolesService = inject(RolesService);
  private readonly userRolesService = inject(UserRolesService);
  private readonly destroyRef = inject(DestroyRef);

  // expose helpers to template
  readonly isBusinessRole = isBusinessRole;
  readonly initials = userInitials;
  readonly fullName = userFullName;

  // ── search state ───────────────────────────────────────────────────────
  readonly searchCtrl = new FormControl<string>('', { nonNullable: true });
  readonly searchLoading = signal(false);
  readonly searchError = signal<string | null>(null);
  readonly searchResults = signal<User[]>([]);

  // ── selected user state ────────────────────────────────────────────────
  readonly selectedUser = signal<User | null>(null);
  readonly availableRoles = signal<RoleDTO[]>([]);
  readonly rolesLoading = signal(false);
  readonly selectedRoles = signal<Set<string>>(new Set());
  readonly rolesFilterCtrl = new FormControl<string>('', { nonNullable: true });
  private readonly rolesFilterSignal = signal('');

  readonly submitting = signal(false);
  readonly serverError = signal<string | null>(null);
  readonly successMessage = signal<string | null>(null);

  readonly visibleRoles = computed(() => {
    const q = this.rolesFilterSignal().toLowerCase().trim();
    if (!q) return this.availableRoles();
    return this.availableRoles().filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description?.toLowerCase().includes(q) ?? false),
    );
  });

  readonly rolesDirty = computed(() => {
    const u = this.selectedUser();
    if (!u) return false;
    const initial = new Set((u.roles ?? []).map(stripRolePrefix));
    const target = this.selectedRoles();
    if (initial.size !== target.size) return true;
    for (const r of target) if (!initial.has(r)) return true;
    return false;
  });

  // ── lifecycle ──────────────────────────────────────────────────────────
  ngOnInit(): void {
    this.searchCtrl.valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((q) => this.runSearch(q));

    this.rolesFilterCtrl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((v) => this.rolesFilterSignal.set(v ?? ''));

    // Preload roles in the background — used as soon as a user is picked.
    this.loadAvailableRoles();
  }

  // ── search ─────────────────────────────────────────────────────────────
  runSearch(query: string): void {
    const q = (query ?? '').trim();
    if (q.length < 2) {
      this.searchResults.set([]);
      return;
    }
    this.searchLoading.set(true);
    this.searchError.set(null);
    this.usersService
      .list({
        page: 1,
        size: 10,
        search: rsqlTextSearch(USER_SEARCH_FIELDS, q) ?? undefined,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.searchResults.set(res.data ?? []);
          this.searchLoading.set(false);
        },
        error: (err: unknown) => {
          this.searchLoading.set(false);
          this.searchResults.set([]);
          this.searchError.set(toErrorMessage(err, 'Recherche impossible.'));
        },
      });
  }

  // ── selection ──────────────────────────────────────────────────────────
  selectUser(user: User): void {
    this.selectedUser.set(user);
    this.serverError.set(null);
    this.successMessage.set(null);
    this.selectedRoles.set(
      new Set((user.roles ?? []).map(stripRolePrefix)),
    );
    this.rolesFilterCtrl.setValue('');
    this.rolesFilterSignal.set('');
    // Clear search results so the panel takes focus.
    this.searchResults.set([]);
    this.searchCtrl.setValue('', { emitEvent: false });
  }

  clearSelection(): void {
    this.selectedUser.set(null);
    this.selectedRoles.set(new Set());
    this.serverError.set(null);
    this.successMessage.set(null);
  }

  // ── roles ──────────────────────────────────────────────────────────────
  private loadAvailableRoles(): void {
    this.rolesLoading.set(true);
    this.rolesService.list().subscribe({
      next: (roles) => {
        // Sort once here so `visibleRoles` doesn't re-sort on every keystroke.
        this.availableRoles.set([...(roles ?? [])].sort(byBusinessThenAlpha));
        this.rolesLoading.set(false);
      },
      error: () => this.rolesLoading.set(false),
    });
  }

  toggleRole(name: string): void {
    const next = new Set(this.selectedRoles());
    if (next.has(name)) next.delete(name);
    else next.add(name);
    this.selectedRoles.set(next);
    this.successMessage.set(null);
  }

  isRoleSelected(name: string): boolean {
    return this.selectedRoles().has(name);
  }

  // ── save ───────────────────────────────────────────────────────────────
  save(): void {
    const u = this.selectedUser();
    if (!u || !this.rolesDirty()) return;
    const initial = new Set((u.roles ?? []).map(stripRolePrefix));
    const target = this.selectedRoles();
    const added = [...target].filter((r) => !initial.has(r));
    const removed = [...initial].filter((r) => !target.has(r));

    this.submitting.set(true);
    this.serverError.set(null);
    this.successMessage.set(null);

    const ops: Observable<void>[] = [];
    if (added.length) {
      ops.push(
        this.userRolesService.assign({
          userIdentifier: u.username,
          roleNames: added,
        }),
      );
    }
    if (removed.length) {
      ops.push(
        this.userRolesService.remove({
          userIdentifier: u.username,
          roleNames: removed,
        }),
      );
    }

    forkJoin(ops).subscribe({
      next: () => {
        this.submitting.set(false);
        // refresh user from server to get authoritative roles
        this.usersService.get(u.id).subscribe({
          next: (refreshed) => {
            this.selectedUser.set(refreshed);
            this.selectedRoles.set(
              new Set((refreshed.roles ?? []).map(stripRolePrefix)),
            );
            this.successMessage.set(
              `Rôles de ${refreshed.username} mis à jour (+${added.length}, -${removed.length}).`,
            );
          },
          error: () => {
            // optimistic update fallback
            this.successMessage.set(
              `Opération envoyée. Rafraîchir l'utilisateur pour vérifier.`,
            );
          },
        });
      },
      error: (err: unknown) => {
        this.submitting.set(false);
        this.serverError.set(
          toErrorMessage(err, 'La mise à jour des rôles a échoué.'),
        );
      },
    });
  }
}
